import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Users, UserCheck, MousePointerClick, Megaphone,
  MapPin, GraduationCap, Briefcase, HelpCircle, TrendingUp, MailCheck, Award,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * Webinar analytics — overview (cross-webinar) + per-webinar drill-down.
 * Read-only. Mirrors AnalyticsTab's flat CSS-bar style (no chart lib) for a
 * native look. Backed by:
 *   GET /admin/webinars/analytics/overview
 *   GET /admin/webinars/{id}/analytics
 */

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function Tile({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-[#F9FAFB] rounded-xl p-4">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">
        {Icon && <Icon size={13} className="text-[#965EA7]" />} {label}
      </div>
      <div className="font-display text-2xl font-bold text-[#373A45] mt-1">{value}</div>
      {sub != null && <div className="text-xs text-[#9CA3AF] mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-[#373A45] inline-flex items-center gap-2 mb-4">
        {Icon && <Icon size={16} className="text-[#965EA7]" />} {title}
      </h3>
      {children}
    </div>
  );
}

function BarList({ block, color = "#965EA7", emptyText = "Nu există date.", onItemClick }) {
  // `block` = { items: [{label,count}], shown, distinct }. Tolerates a bare array too.
  const items = Array.isArray(block) ? block : (block?.items || []);
  if (items.length === 0) return <div className="text-sm text-[#9CA3AF]">{emptyText}</div>;
  const max = Math.max(1, ...items.map((r) => r.count || 0));
  const shown = block?.shown ?? items.length;
  const distinct = block?.distinct ?? items.length;
  const truncated = distinct > shown;
  const clickable = typeof onItemClick === "function";
  return (
    <div>
      <div className="space-y-2.5">
        {items.map((r, i) => (
          <div key={`${r.label}-${i}`} className="flex items-center gap-3">
            {clickable ? (
              <button
                type="button"
                onClick={() => onItemClick(r)}
                className="w-40 shrink-0 text-left text-sm text-[#3F8EBA] hover:text-[#1f5d80] hover:underline truncate"
                title={`${r.label} — vezi detalii`}
              >
                {r.label}
              </button>
            ) : (
              <div className="w-40 shrink-0 text-sm text-[#373A45] truncate" title={r.label}>{r.label}</div>
            )}
            <div className="flex-1 bg-[#F5F6F6] rounded-full h-2.5 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${((r.count || 0) / max) * 100}%`, minWidth: r.count ? "6px" : 0, background: color }} />
            </div>
            <div className="w-10 shrink-0 text-right text-sm font-semibold text-[#373A45]">{r.count}</div>
          </div>
        ))}
      </div>
      {truncated && (
        <div className="text-xs text-[#9CA3AF] mt-3">Top {shown} din {distinct}</div>
      )}
    </div>
  );
}

// Hierarchical geo explorer: județe → localități (în județ) → școli (în localitate).
function GeoExplorer({ webinarId }) {
  const [county, setCounty] = useState(null);
  const [locality, setLocality] = useState(null);
  const qs = county
    ? (locality
        ? `?county=${encodeURIComponent(county)}&locality=${encodeURIComponent(locality)}`
        : `?county=${encodeURIComponent(county)}`)
    : "";
  const { data, loading } = useEndpoint(`/admin/webinars/${webinarId}/analytics/geo${qs}`);

  const crumb = (label, onClick, active) => (
    <button
      type="button"
      onClick={onClick}
      disabled={active}
      className={`text-xs font-semibold ${active ? "text-[#373A45]" : "text-[#3F8EBA] hover:underline"}`}
    >
      {label}
    </button>
  );

  let onItemClick;
  if (!county) {
    onItemClick = (r) => { setCounty(r.label); setLocality(null); };
  } else if (!locality) {
    onItemClick = (r) => setLocality(r.label);
  } else {
    onItemClick = undefined; // schools are the leaf level — not clickable
  }

  const color = locality ? "#1f5d80" : county ? "#3F8EBA" : "#965EA7";
  const empty = locality ? "Nicio școală." : county ? "Nicio localitate." : "Niciun județ.";

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {crumb("Județe", () => { setCounty(null); setLocality(null); }, !county)}
        {county && <span className="text-[#9CA3AF] text-xs">/</span>}
        {county && crumb(county, () => setLocality(null), !locality)}
        {locality && <span className="text-[#9CA3AF] text-xs">/</span>}
        {locality && crumb(locality, () => {}, true)}
      </div>
      {loading ? (
        <div className="text-sm text-[#9CA3AF]"><Loader2 className="inline animate-spin" size={14} /> Se încarcă...</div>
      ) : (
        <BarList block={data} color={color} emptyText={empty} onItemClick={onItemClick} />
      )}
    </div>
  );
}

function Funnel({ funnel }) {
  const f = funnel || {};
  const steps = [
    { label: "Înscriși", value: f.registered || 0, icon: Users, color: "#965EA7" },
    { label: "Email trimis", value: f.email_sent || 0, icon: MailCheck, color: "#3F8EBA" },
    { label: "Link accesat", value: f.link_clicked || 0, icon: MousePointerClick, color: "#F5C76E" },
    { label: "Prezenți", value: f.attended || 0, icon: UserCheck, color: "#519872" },
  ];
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <div className="space-y-3">
      {steps.map((s) => (
        <div key={s.label} className="flex items-center gap-3">
          <div className="w-28 shrink-0 inline-flex items-center gap-1.5 text-sm text-[#373A45]">
            <s.icon size={14} style={{ color: s.color }} /> {s.label}
          </div>
          <div className="flex-1 bg-[#F5F6F6] rounded-full h-3 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(s.value / max) * 100}%`, minWidth: s.value ? "8px" : 0, background: s.color }} />
          </div>
          <div className="w-10 shrink-0 text-right text-sm font-semibold text-[#373A45]">{s.value}</div>
        </div>
      ))}
      <div className="flex flex-wrap gap-2 pt-1">
        <span className="inline-flex items-center gap-1 rounded-full bg-[#519872]/15 text-[#2F6B4E] px-3 py-1 text-xs font-semibold">
          <Award size={12} /> Rată prezență {f.attendance_rate ?? 0}%
        </span>
        <span className="inline-flex rounded-full bg-[#F5C76E]/30 text-[#7A5A1B] px-3 py-1 text-xs font-semibold">Absenți {f.no_show || 0}</span>
        <span className="inline-flex rounded-full bg-[#E63946]/15 text-[#7A1F26] px-3 py-1 text-xs font-semibold">Anulați {f.cancelled || 0}</span>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="py-16 text-center text-[#6B7280]">
      <Loader2 className="inline animate-spin" size={18} /> Se încarcă analiza...
    </div>
  );
}

function useEndpoint(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(path)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("Nu am putut încărca analiza."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);
  return { data, loading };
}

// ---------------------------------------------------------------------------
// Overview (cross-webinar)
// ---------------------------------------------------------------------------

export function WebinarAnalyticsOverview({ onSelectWebinar, onBack }) {
  const { data, loading } = useEndpoint("/admin/webinars/analytics/overview");
  const t = data?.totals || {};
  return (
    <div className="space-y-5" data-testid="webinar-analytics-overview">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#373A45]">
        <ArrowLeft size={15} /> Înapoi la webinarii
      </button>
      {loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Tile label="Total înscriși" value={(t.total_registered || 0).toLocaleString("ro-RO")} icon={Users} />
            <Tile label="Rată medie prezență" value={`${t.avg_attendance_rate ?? 0}%`} icon={UserCheck} />
            <Tile label="Webinarii desfășurate" value={t.webinars_held || 0} icon={TrendingUp} />
          </div>

          <SectionCard title="Top webinarii" icon={Award} testid="overview-top-webinars">
            {(data?.top_webinars || []).length === 0 ? (
              <div className="text-sm text-[#9CA3AF]">Niciun webinar cu înscrieri încă.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[#6B7280] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-2">Webinar</th>
                      <th className="text-right py-2">Înscriși</th>
                      <th className="text-right py-2">Prezenți</th>
                      <th className="text-right py-2">Rată</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F1F1]">
                    {data.top_webinars.map((w) => (
                      <tr key={w.id} className="hover:bg-[#FAFAFB] cursor-pointer" onClick={() => onSelectWebinar && onSelectWebinar(w)}>
                        <td className="py-2">
                          <div className="font-semibold text-[#373A45]">{w.title}</div>
                          <div className="text-xs text-[#9CA3AF]">{w.date}</div>
                        </td>
                        <td className="py-2 text-right font-semibold text-[#373A45]">{w.registered}</td>
                        <td className="py-2 text-right text-[#373A45]">{w.attended}</td>
                        <td className="py-2 text-right text-[#2F6B4E] font-semibold">{w.attendance_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Județe" icon={MapPin} testid="overview-counties">
              <BarList block={data?.counties} color="#3F8EBA" />
            </SectionCard>
            <SectionCard title="Localități" icon={MapPin} testid="overview-localities">
              <BarList block={data?.localities} color="#1f5d80" />
            </SectionCard>
            <SectionCard title="Surse (UTM)" icon={Megaphone} testid="overview-sources">
              <BarList block={data?.sources} color="#965EA7" />
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drill-down (per-webinar)
// ---------------------------------------------------------------------------

export function WebinarAnalyticsDrilldown({ webinar, onBack }) {
  const { data, loading } = useEndpoint(`/admin/webinars/${webinar.id}/analytics`);
  const consent = data?.consent || { yes: 0, no: 0 };
  return (
    <div className="space-y-5" data-testid="webinar-analytics-drilldown">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#373A45]">
        <ArrowLeft size={15} /> Înapoi
      </button>
      <div>
        <h2 className="font-display text-xl font-bold text-[#373A45]">{webinar.title}</h2>
        <div className="text-sm text-[#9CA3AF]">{webinar.date} · analiză înscrieri</div>
      </div>
      {loading ? <Loading /> : (
        <>
          <SectionCard title="Funnel & prezență" icon={UserCheck} testid="drill-funnel">
            <Funnel funnel={data?.funnel} />
          </SectionCard>

          <SectionCard title="Explorează geografic (județ → localitate → școală)" icon={MapPin} testid="drill-geo-explorer">
            <GeoExplorer webinarId={webinar.id} />
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Top localități" icon={MapPin} testid="drill-localities">
              <BarList block={data?.geo?.localities} color="#3F8EBA" />
            </SectionCard>
            <SectionCard title="Top școli / instituții" icon={GraduationCap} testid="drill-schools">
              <BarList block={data?.geo?.schools} color="#1f5d80" />
            </SectionCard>
            <SectionCard title="Surse (UTM)" icon={Megaphone} testid="drill-sources">
              <BarList block={data?.sources} color="#965EA7" />
            </SectionCard>
            <SectionCard title="Campanii" icon={Megaphone} testid="drill-campaigns">
              <BarList block={data?.campaigns} color="#7A4986" emptyText="Fără UTM campaign." />
            </SectionCard>
            <SectionCard title="Rol participant" icon={Briefcase} testid="drill-positions">
              <BarList block={data?.profile?.positions} color="#519872" />
            </SectionCard>
            <SectionCard title="Cum au aflat" icon={HelpCircle} testid="drill-referral">
              <BarList block={data?.profile?.referral_sources} color="#2F6B4E" />
            </SectionCard>
          </div>

          <SectionCard title="Consimțământ marketing" icon={MailCheck} testid="drill-consent">
            <div className="flex gap-3">
              <Tile label="Au acceptat" value={consent.yes} />
              <Tile label="Nu au acceptat" value={consent.no} />
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
